import React, { useEffect, useState, useCallback, useMemo } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import Cookies from 'js-cookie';
import axios from 'axios';
import toast from 'react-hot-toast';

// Components
import FolderItem from "./folderItem";
import LinkItem from "./linkItem";
import ShortcutItem from "./shortcutItem";
import DriveFile from "./driveFile";
import MultiSelectToast from "./MultiSelectToast";
import ItemCreationPanel from "./ItemCreationPanel";
import TsvUploadDialog from "./TsvUploadDialog";
import { ScrollArea } from "./ui/scroll-area";

// Zustand Store
import useCanvasStore from "../stores/canvasStore";

// Button indices for keyboard navigation
const BUTTON = { folder: -3, sheet: -2, doc: -1 };

// Redux - Only for drive files (keyboard navigation moved to Zustand)
import { fetchOrSyncDriveFiles, syncDriveFiles } from "../redux/actions/driveFiles.action";

// Services
import { bulkCreateFolders } from "../services/canvas.service";

import { BACKEND_API_URL } from "../utils/api";

const ItemColumn = ({ depth }) => {
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const { folderId } = useParams();
    const accessToken = Cookies.get('access_token');

    // ============ Zustand Store State ============
    const {
        items,
        selectedPath,
        selectedItems,
        selectionMode,
        focusedIndex,
        createFolder,
        createLink,
        toggleItemSelection,
        setBulkAssignDialogOpen,
        fetchCanvas,
        setKeyboardNavigationActive,
        setButtonHandlers,
        setFocusedIndex
    } = useCanvasStore();

    // Get items at this depth (already sorted and filtered by backend)
    // Backend sends items in visual order: shortcuts → links → folders (then by user sort criteria)
    const itemsAtDepth = items[depth] || [];

    // Parse focusedIndex for this column (used for button focus)
    const parsedFocus = useMemo(() => {
        if (!focusedIndex) return null;
        const [focusDepth, focusIdx] = focusedIndex.split('_').map(Number);
        if (focusDepth !== depth) return null;
        return focusIdx;
    }, [focusedIndex, depth]);

    // Current folder ID (parent of items at this depth)
    const currentFolderId = depth > 1 && selectedPath[depth - 2] !== undefined
        ? selectedPath[depth - 2]
        : null;

    // Drive files for this folder
    const driveData = useSelector((state) =>
        currentFolderId ? state.driveFiles?.byFolderId?.[currentFolderId] : null
    );
    const driveFiles = driveData?.driveFiles || [];
    const gdriveFolderId = driveData?.gdriveFolderId;
    const driveSyncing = driveData?.syncing || false;

    // Local state for item creation
    const [newTaskOrLinkName, setNewTaskOrLinkName] = useState('');
    const [isAddingFolder, setIsAddingFolder] = useState(false);
    const [isAddingLinkOrTask, setIsAddingLinkOrTask] = useState(false);
    const [isAddingSheet, setIsAddingSheet] = useState(false);
    const [isAddingDoc, setIsAddingDoc] = useState(false);
    const [loading, setIsLoading] = useState(false);
    const [isBookmarked, setIsBookmarked] = useState(false);
    const [tsvDialogOpen, setTsvDialogOpen] = useState(false);

    // Button focus state - derived from parsedFocus
    const focusedButton = useMemo(() => {
        if (parsedFocus === null || parsedFocus >= 0) return null;
        if (parsedFocus === BUTTON.folder) return 'folder';
        if (parsedFocus === BUTTON.sheet) return 'sheet';
        if (parsedFocus === BUTTON.doc) return 'doc';
        return null;
    }, [parsedFocus]);

    // ============ Keyboard Navigation State (Zustand) ============
    useEffect(() => {
        if (isAddingFolder || isAddingLinkOrTask || isAddingDoc || isAddingSheet) {
            setKeyboardNavigationActive(false);
        } else {
            setKeyboardNavigationActive(true);
        }
    }, [isAddingDoc, isAddingFolder, isAddingLinkOrTask, isAddingSheet, setKeyboardNavigationActive]);

    // ============ Drive Files ============
    // Only load the cached DB copy here; Google Drive sync runs when the folder's
    // details modal is opened (double/right click) or on manual refresh
    useEffect(() => {
        if (currentFolderId) {
            dispatch(fetchOrSyncDriveFiles(currentFolderId, { sync: false }));
        }
    }, [currentFolderId, dispatch]);

    // Manual refresh bypasses the backend's sync throttle
    const handleRefreshDriveFiles = useCallback(() => {
        if (currentFolderId) {
            dispatch(syncDriveFiles(currentFolderId, { force: true }));
        }
    }, [currentFolderId, dispatch]);

    // ============ Bookmark Status ============
    useEffect(() => {
        if (currentFolderId && depth > 1) {
            // Find the current folder in the previous depth's items
            const parentDepthItems = items[depth - 1] || [];
            const currentFolder = parentDepthItems.find(f => f.id === currentFolderId);
            if (currentFolder) {
                setIsBookmarked(currentFolder.is_bookmarked || false);
            }
        } else {
            setIsBookmarked(false);
        }
    }, [currentFolderId, items, depth]);

    // ============ Handlers ============
    const handleAddLinkOrTaskClick = useCallback(() => {
        setIsAddingLinkOrTask(true);
    }, []);

    const handleAddSheet = useCallback(() => {
        setIsAddingSheet(true);
    }, []);

    const handleAddDoc = useCallback(() => {
        setIsAddingDoc(true);
    }, []);

    // Register button handlers for keyboard navigation (after handlers are defined)
    useEffect(() => {
        setButtonHandlers(depth, {
            folder: handleAddLinkOrTaskClick,
            sheet: handleAddSheet,
            doc: handleAddDoc
        });
    }, [depth, setButtonHandlers, handleAddLinkOrTaskClick, handleAddSheet, handleAddDoc]);

    const handleTaskOrLinkChange = (event) => {
        setNewTaskOrLinkName(event.target.value);
    };

    const isURL = (str) => {
        try {
            new URL(str);
            return true;
        } catch (error) {
            return false;
        }
    };

    const handleCreateNewSheet = async (name, is_sheet) => {
        try {
            const response = await axios.post(
                `${BACKEND_API_URL}/api/google/create_new_document/`,
                { name, is_sheet },
                {
                    headers: {
                        Authorization: `Token ${accessToken}`,
                        'Content-Type': 'application/json',
                    },
                }
            );
            const sheetId = response.data.id;
            return is_sheet
                ? `https://docs.google.com/spreadsheets/d/${sheetId}`
                : `https://docs.google.com/document/d/${sheetId}`;
        } catch (error) {
            console.log(error);
            toast.error("Error creating document");
            throw error;
        }
    };

    // Handle bulk folder creation from newline-separated text
    const handleBulkFolderCreation = async (folderNames, parentId) => {
        if (folderNames.length === 0) return;

        setIsLoading(true);
        try {
            const foldersToCreate = folderNames
                .map(name => name.trim())
                .filter(name => name !== '')
                .map(name => ({ name }));
            if (foldersToCreate.length === 0) return;
            const response = await bulkCreateFolders(foldersToCreate, parentId);

            toast.success(`Created ${response.success_count} folder(s)`);

            // Re-fetch to get updated data
            fetchCanvas(currentFolderId);
        } catch (error) {
            console.error('Error creating folders:', error);
            toast.error(error.response?.data?.error || 'Failed to create folders');
        } finally {
            setIsLoading(false);
        }
    };

    const handleCreateLinkOrTask = async (event) => {
        // Handle ESC key to cancel input
        if (event.key === 'Escape') {
            event.preventDefault();
            event.stopPropagation();
            setNewTaskOrLinkName('');
            setIsAddingLinkOrTask(false);
            setIsAddingSheet(false);
            setIsAddingDoc(false);
            return;
        }

        // Allow Shift+Enter for new lines in textarea
        if (event.key === 'Enter' && event.shiftKey) {
            return; // Let the default behavior create a new line
        }

        if (event.key === 'Enter' && newTaskOrLinkName.trim() !== '') {
            event.preventDefault();
            event.stopPropagation();

            const parentId = depth > 1 ? selectedPath[depth - 2] : null;

            try {
                if (isAddingSheet || isAddingDoc) {
                    // For Sheet/Doc: create, open in new tab, then close input
                    setIsLoading(true);
                    const url = await handleCreateNewSheet(newTaskOrLinkName, isAddingSheet);
                    await createLink(url, newTaskOrLinkName, parentId, depth);
                    window.open(url, '_blank');
                    setIsLoading(false);
                    // Close input after sheet/doc creation
                    setNewTaskOrLinkName('');
                    setIsAddingLinkOrTask(false);
                    setIsAddingSheet(false);
                    setIsAddingDoc(false);
                } else {
                    // For Folder/Link: create and keep input open for continuous creation
                    const parts = newTaskOrLinkName.split(",");
                    if (isURL(parts[0])) {
                        await createLink(parts[0], parts[1] || null, parentId, depth);
                    } else {
                        // Create folder
                        const folderName = newTaskOrLinkName.trim();
                        await createFolder(folderName, parentId, depth);
                        // Focus on the newly created folder (it's added at index 0)
                        setFocusedIndex(`${depth}_0`);
                    }
                    // Clear input but keep it open for next folder/link
                    setNewTaskOrLinkName('');
                }
            } catch (error) {
                console.error('Error creating item:', error);
                setIsLoading(false);
            }
        }
    };

    // Toggle bookmark for current folder
    const handleToggleBookmark = async () => {
        if (!currentFolderId) return;

        const newIsBookmarked = !isBookmarked;
        setIsBookmarked(newIsBookmarked);

        try {
            await axios.post(
                `${BACKEND_API_URL}/api/item/bookmark/toggle/${currentFolderId}`,
                {},
                {
                    headers: {
                        'Authorization': `Token ${accessToken}`,
                        'Content-Type': 'application/json',
                    },
                }
            );
            toast.success(newIsBookmarked ? "Folder Bookmarked" : "Bookmark Removed");
        } catch (error) {
            console.error(error);
            setIsBookmarked(!newIsBookmarked);
            toast.error("Error Updating Bookmark");
        }
    };

    // TSV Upload handlers
    const handleTsvUploadClick = () => setTsvDialogOpen(true);
    const handleTsvDialogClose = () => setTsvDialogOpen(false);

    const handleTsvUpload = async (structure) => {
        try {
            // Use each folder's hierarchy number as its temp_id; the parent number
            // (already null for roots) maps directly to parent_temp_id.
            const foldersToCreate = structure.folders.map((folder) => ({
                name: folder.name,
                temp_id: folder.number,
                ...(folder.parentNumber && { parent_temp_id: folder.parentNumber })
            }));

            const response = await bulkCreateFolders(foldersToCreate, currentFolderId);

            toast.success(`Created ${response.success_count} folders`);

            // Re-fetch to get updated data
            fetchCanvas(currentFolderId);
        } catch (error) {
            console.error('Error creating folder structure:', error);
            toast.error(error.response?.data?.error || 'Failed to create folder structure');
            throw error;
        }
    };

    // Bulk assign handler
    const handleAssignClick = useCallback(() => {
        if (selectedItems.length < 1) {
            toast("Please select at least 1 item to use bulk assign");
            return;
        }

        const hasShortcuts = selectedItems.some(item => item.item_type === 'shortcut');
        if (hasShortcuts) {
            toast.error("Bulk assign not supported for shortcuts. Please select only folders/links.");
            return;
        }

        setBulkAssignDialogOpen(true);
    }, [selectedItems, setBulkAssignDialogOpen]);

    // Check if selected items are at this depth
    const hasSelectedItemsAtThisDepth = selectedItems.length > 0 && selectedItems[0].depth === depth;

    return (
        <div data-name="item-column-container" className="flex flex-col h-full pb-2 bg-background">
            {/* Multi-select feedback banner */}
            <MultiSelectToast
                selectedItems={selectedItems}
                selectionMode={selectionMode}
                hasSelectedItemsAtThisDepth={hasSelectedItemsAtThisDepth}
                onAssignClick={handleAssignClick}
            />

            {/* Creation panel */}
            <div className="border-b border-border">
                <ItemCreationPanel
                    isAddingLinkOrTask={isAddingLinkOrTask}
                    isAddingSheet={isAddingSheet}
                    isAddingDoc={isAddingDoc}
                    loading={loading}
                    newTaskOrLinkName={newTaskOrLinkName}
                    handleTaskOrLinkChange={handleTaskOrLinkChange}
                    handleCreateLinkOrTask={handleCreateLinkOrTask}
                    handleAddLinkOrTaskClick={handleAddLinkOrTaskClick}
                    handleAddSheet={handleAddSheet}
                    handleAddDoc={handleAddDoc}
                    setIsAddingLinkOrTask={setIsAddingLinkOrTask}
                    setIsAddingSheet={setIsAddingSheet}
                    setIsAddingDoc={setIsAddingDoc}
                    focusedButton={focusedButton}
                    folderId={currentFolderId}
                    isBookmarked={isBookmarked}
                    onToggleBookmark={handleToggleBookmark}
                    gdriveFolderId={gdriveFolderId}
                    driveSyncing={driveSyncing}
                    onRefreshDriveFiles={handleRefreshDriveFiles}
                    onTsvUploadClick={handleTsvUploadClick}
                />
            </div>

            {/* TSV Upload Dialog */}
            <TsvUploadDialog
                open={tsvDialogOpen}
                onClose={handleTsvDialogClose}
                onUpload={handleTsvUpload}
            />

            {/* Items scroll area */}
            <ScrollArea
                data-name="items-scroll-area"
                className="flex-1"
            >
                <div className="px-1 pt-1 space-y-0">
                    {/* Google Drive Files */}
                    {driveFiles.map(file => (
                        <DriveFile key={file.id} file={file} />
                    ))}

                    {/* All items in visual order (shortcuts → links → folders, sent by backend) */}
                    {/* Components now read their data directly from Zustand store */}
                    {itemsAtDepth.map((item, index) => {
                        if (item.item_type === 'shortcut') {
                            return (
                                <ShortcutItem
                                    key={item.id}
                                    id={item.id}
                                    depth={depth}
                                    itemIndex={index}
                                />
                            );
                        } else if (item.item_type === 'link') {
                            return (
                                <LinkItem
                                    key={item.id}
                                    id={item.id}
                                    depth={depth}
                                    itemIndex={index}
                                />
                            );
                        } else if (item.item_type === 'folder') {
                            return (
                                <FolderItem
                                    key={item.id}
                                    id={item.id}
                                    depth={depth}
                                    itemIndex={index}
                                />
                            );
                        }
                        return null;
                    })}
                </div>
            </ScrollArea>
        </div>
    );
};

export default ItemColumn;
